// =============================================================================
// 実績(マイルストーン)。純粋なロジック（演出・発火条件の判定はmain.ts/hud.ts）。
// 「生活基盤が整った」「島々を渡り歩いた」「レア素材を手に入れた」——プレイの節目を
// 祝って達成感を作る。達成は周回(はじめから)をまたいで永続する(Steam実績と同じ感覚)。
// =============================================================================

export interface FeatDef {
  id: string;
  icon: string;
  name: string;
  desc: string; // 達成時バナー・一覧(達成済み)の一行
  hint: string; // 一覧(未達成)に出す取り方のヒント
}

// 定義順 = 一覧の表示順(序盤→終盤)。id はセーブに入るので変更しないこと。
export const FEATS: FeatDef[] = [
  { id: 'first_craft',    icon: '🔨', name: 'はじめての工作',     desc: '素材から道具を初めて作り出した', hint: '素材を集めてクラフトする' },
  { id: 'camp_ready',     icon: '🏕', name: '生活基盤',           desc: '焚き火と給水器が揃った——夜も渇きも怖くない', hint: '焚き火と給水器を設置する' },
  { id: 'home',           icon: '🛖', name: '我が家',             desc: 'シェルターを建てた——この島の我が家だ', hint: '板6・ロープ4・繊維6でシェルターを建てる' },
  { id: 'first_cook',     icon: '🍖', name: '島の料理人',         desc: '焚き火で初めて調理した', hint: '魚や肉を焚き火のそばで調理する' },
  { id: 'tool_master',    icon: '🧰', name: '道具マスター',       desc: '斧・ナイフ・銛・釣り竿を全て揃えた', hint: '4種の道具を全てクラフトする' },
  { id: 'first_fish',     icon: '🎣', name: 'はじめての釣果',     desc: '初めて魚を釣り上げた', hint: '釣り竿を作って水辺で釣る' },
  { id: 'first_sail',     icon: '⛵', name: '処女航海',           desc: 'いかだで海へ漕ぎ出した', hint: 'いかだに乗って海を渡る' },
  { id: 'new_island',     icon: '🏝', name: '新天地',             desc: '漂着島の外の島に初めて上陸した', hint: 'いかだで別の島へ渡る' },
  { id: 'all_islands',    icon: '🗺', name: '群島の覇者',         desc: 'すべての島に上陸した', hint: '世界のすべての島に上陸する' },
  { id: 'peak_view',      icon: '🔭', name: '山頂の絶景',         desc: '高峰から島々を見渡した', hint: '高い山の上へ登る' },
  { id: 'summit',         icon: '🗻', name: '天へ至る峰',         desc: '東の天峰島の頂に立った——世界の最高点だ', hint: '世界で最も高い山を登りきる' },
  { id: 'first_salvage',  icon: '📦', name: 'サルベージ屋',       desc: '海底の沈没物資を初めて回収した', hint: '海底に沈むクレートを開ける' },
  { id: 'first_treasure', icon: '💎', name: 'トレジャーハンター', desc: '深海の財宝を引き上げた', hint: '💎の眠る深みへ潜る' },
  { id: 'first_letter',   icon: '🍾', name: '海からの便り',       desc: '瓶の手紙を読んだ——沈んだ積み荷の手がかりだ', hint: '朝、浜に流れ着く漂着物を調べる' },
  { id: 'relic_hunter',   icon: '🗺', name: '手がかりを追う者',   desc: '手紙の示す海域で船乗りの遺品を見つけた', hint: '瓶の手紙が示す❓の海域へ潜る' },
  { id: 'sea_sweeper',    icon: '🧭', name: '海底を知り尽くす',   desc: 'すべての沈没物資と財宝を回収した', hint: '全てのクレートを開封する' },
  { id: 'shark_slayer',   icon: '🔱', name: 'シャークハンター',   desc: 'サメを銛で仕留めた', hint: '銛を構えてサメに反撃する' },
  { id: 'boss_slayer',    icon: '👑', name: '海溝の主討伐',       desc: '海溝の主を討ち取った', hint: '外洋の最深部に潜む主を倒す' },
  { id: 'first_rare',     icon: '✨', name: '島の秘宝',           desc: 'レア素材を初めて手に入れた', hint: '各島には固有の秘宝が眠っている' },
  { id: 'rare_all',       icon: '🌈', name: '三つの秘宝',         desc: '真珠・黒曜石・楽園の花を全て集めた', hint: '3種のレア素材を全て手に入れる' },
  { id: 'day3',           icon: '🌅', name: '三日目の朝',         desc: '漂着から3日目の朝を迎えた', hint: '3日間生き延びる' },
  { id: 'week_survivor',  icon: '🏅', name: '一週間の漂流',       desc: '7日間生き延びた', hint: '7日間生き延びる' },
  { id: 'repair_half',    icon: '🔧', name: '出航が見えてきた',   desc: '難破船の修理が半分まで進んだ', hint: '難破船へ修理素材を運び込む' },
  { id: 'escape',         icon: '🎉', name: '生還',               desc: '修理した船で島々から脱出した', hint: '難破船を完全に修理する' },
];

const KEY = 'side6-feats';
const unlocked = new Set<string>();
let loaded = false;

// localStorage の無い環境(node --test)や容量例外でも純ロジックとして動き続ける
function persist(): void {
  try { localStorage.setItem(KEY, JSON.stringify([...unlocked])); } catch { /* ignore */ }
}
export function loadFeats(): void {
  loaded = true;
  try {
    const d = JSON.parse(localStorage.getItem(KEY) || '[]');
    if (Array.isArray(d)) for (const id of d) if (typeof id === 'string' && FEATS.some((f) => f.id === id)) unlocked.add(id);
  } catch { /* ignore */ }
}

// 達成。新規達成なら定義を返す(呼び出し側がバナー/音を出す)。既達成・未知idはnull。
export function unlockFeat(id: string): FeatDef | null {
  if (!loaded) loadFeats();
  const def = FEATS.find((f) => f.id === id);
  if (!def || unlocked.has(id)) return null;
  unlocked.add(id);
  persist();
  return def;
}

export function isUnlocked(id: string): boolean { return unlocked.has(id); }
export function unlockedCount(): number { return unlocked.size; }
export function featList(): { def: FeatDef; unlocked: boolean }[] {
  return FEATS.map((def) => ({ def, unlocked: unlocked.has(def.id) }));
}

// テスト/デバッグ用: 達成状態を全消去(永続も消す)
export function resetFeats(): void {
  unlocked.clear();
  loaded = true; // reset後にstorageから読み戻さない(テストの決定性)
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
